<template>
  <div class="d-inline">
    <label v-if="label" class="input-label" :class="{ regular: regularLabel }">
      {{ label }}
    </label>
    <div
      class="custom-time-picker-container"
      :class="[classContainer, error ? 'invalidated' : '']"
    >
      <em :class="getTimeViewIcon" />
      <div class="dropdown">
        <input
          id="dropDownTimeSelector"
          v-model="valueFormatted"
          type="text"
          :placeholder="placeholder"
          class="form-control"
          data-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
          readonly
          :disabled="disabled"
        />
        <span v-if="evalClearValue" class="clear-time" @click="onClearValue">
          <em class="fa fa-times" />
        </span>
        <div
          class="dropdown-menu no-border dropdown-menu-right custom-dropdown-menu"
          role="menu"
          aria-labelledby="dropDownTimeSelector"
        >
          <div class="time-container">
            <div
              v-for="(group, groupIndex) in timeArray"
              :key="'time-group' + groupIndex"
              class="time-container__time-row"
            >
              <div
                v-for="(time, timeIndex) in group"
                :key="'time-block' + timeIndex"
                class="time-container__time-hour"
                :class="[{ active: evalSelectedHour(time.value) }]"
                @click="onSelectTime(time)"
              >
                <span>{{ time.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Error v-if="error" variant="no-border" :message="customErrorMsg" />
  </div>
</template>

<script>
import _ from 'lodash';
import Error from '@/components/common/Error';
import moment from 'moment';

const START_TIME_VALUE = '07:00';
const END_TIME_VALUE = '19:45';
const FORMAT_HOUR_24 = 'HH:mm';
const FORMAT_HOUR_AM_PM = 'h:mm A';
const HOUR_INTERVAL = 5;
const HOUR_CHUNK_VALUE = 9;

export default {
  name: 'CustomTimePicker',
  components: {
    Error,
  },
  watch: {
    value(newVal, oldVal) {
      if (newVal != oldVal) {
        this.valueFormatted = this.isHourValid(newVal)
          ? this.onFormatInputValue(newVal)
          : '';
      }
    },
  },
  data: () => ({
    timeArray: [],
    valueFormatted: '',
    isInputValid: true,
    is24Format: true,
  }),
  computed: {
    getTimeViewIcon() {
      return this.disabled ? 'fa fa-clock' : 'far fa-clock';
    },
    evalClearValue() {
      return this.isInputValid && !this.disabled;
    },
  },
  methods: {
    evalSelectedHour(hourValue) {
      return moment(this.value, FORMAT_HOUR_24).isSame(
        moment(hourValue, FORMAT_HOUR_24)
      )
        ? true
        : false;
    },
    onSelectTime(item) {
      this.$emit('input', item.value);
    },
    onClearValue() {
      this.valueFormatted = null;
      this.$emit('input', null);
    },
    onFormatInputValue(value) {
      if (this.is24Format) {
        return moment(value, FORMAT_HOUR_24)
          .format(FORMAT_HOUR_AM_PM)
          .toLowerCase();
      } else {
        return value.toLowerCase();
      }
    },
    isHourValid(value) {
      const is24Valid = moment(value, FORMAT_HOUR_24).isValid();
      if (is24Valid) {
        this.is24Format = true;
        return true;
      } else {
        const is12Valid = moment(
          moment(value, FORMAT_HOUR_AM_PM).format(FORMAT_HOUR_AM_PM),
          FORMAT_HOUR_24
        ).isValid();

        if (is12Valid) {
          this.is24Format = false;
          return true;
        }
      }
      return false;
    },
    getHourList(start, end) {
      let startTime = moment(start, FORMAT_HOUR_24);
      let endTime = moment(end, FORMAT_HOUR_24);

      if (endTime.isBefore(startTime)) {
        endTime.add(1, 'day');
      }
      let timeStops = [];

      while (startTime <= endTime) {
        timeStops.push({
          name:
            new moment(startTime).format(FORMAT_HOUR_AM_PM).toLowerCase() + '.',
          value: new moment(startTime).format(FORMAT_HOUR_24),
        });
        startTime.add(HOUR_INTERVAL, 'minutes');
      }
      this.timeArray = _.chunk(timeStops, HOUR_CHUNK_VALUE);
    },
  },
  created() {
    this.getHourList(START_TIME_VALUE, END_TIME_VALUE);
  },
  mounted() {
    if (this.value) {
      const isValueValid = this.isHourValid(this.value);
      if (isValueValid) {
        this.valueFormatted = this.onFormatInputValue(this.value);
      } else {
        this.$emit('input', null); //clear input value
      }
    }
  },
  props: {
    //TODO: supporting for several value types (12-hour, date, string date, etc)
    value: {
      //24-hour format
      type: String,
      default: () => '',
    },
    label: {
      type: String,
      default: () => '',
    },
    regularLabel: {
      type: Boolean,
      default: () => false,
    },
    classContainer: {
      type: String,
      default: () => 'width-container',
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
    placeholder: {
      type: String,
      default: () => ' ',
    },
    error: {
      type: String,
      default: () => '',
    },
    customErrorMsg: {
      type: String,
      default: function() {
        return this.error;
      },
    },
  },
};
</script>

<style scoped lang="scss">
$selectedBaseColor: #ff7058;

.clear-time {
  padding: 0px;
  border: none;
  position: absolute;
  top: 1px;
  right: 32px;
  cursor: pointer;
  em {
    color: #9d9d9d !important;
  }
}

.custom-time-picker-container {
  input[type='text'] {
    background: #fff;
    padding-left: 34px;
    text-align: left;
    font: Regular 16px/24px Lato;
    letter-spacing: 0;
    color: #727272;
    opacity: 1;
    border-radius: 0.25rem;
  }

  .vue__time-picker ::v-deep .dropdown {
    z-index: 99999;
  }

  input[type='text']:disabled {
    background: #e9ecef;
  }

  em {
    color: $selectedBaseColor;
    position: absolute;
    margin-top: 11px;
    margin-left: 11px;
    z-index: 10;
  }
}

.invalidated.custom-time-picker-container ::v-deep input[type='text'] {
  background-color: #f7d7da;
  border: 1px solid #ff0018 !important;
}

.time-container {
  max-width: 690px;
  box-shadow: #727272;
  border: 1px solid #c5c5c5;
  &__header {
    background: #eee;
    text-align: center;
    border-bottom: 1px solid #c5c5c5;
    padding: 10px;
  }

  &__time-row {
    display: flex;
    align-items: flex-start;
    border-right: none;
  }

  &__time-hour {
    cursor: pointer;
    border-right: 1px solid #c5c5c5;
    user-select: none;
    display: flex;
    flex-flow: row wrap;
    justify-content: center;
    width: 77px;
    padding: 10px;
    &:last-child {
      border-right: 0px;
    }
    &.active {
      background: #676767;
      color: #fff;
      &:hover {
        background: #676767;
      }
    }
    &:hover {
      background: #f7f7f7;
    }
    span {
      font-size: 12px !important;
    }
  }
}

.custom-dropdown-menu {
  transform: none !important;
  top: 37px !important;
  right: 0px !important;
  left: unset !important;
}
</style>
